import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';

declare let google: any;

// On-demand loader for the Google Maps JS API (Places library). The script is
// consent-gated (functional category), so nothing is injected at bootstrap:
// a caller requests it once consent is granted, and autocomplete widgets
// initialize whenever loaded$ finally emits — or never, if consent is refused.
@Injectable({
  providedIn: 'root'
})
export class GooglePlacesLoaderService {
  private readonly _loaded$: ReplaySubject<void> = new ReplaySubject<void>(1);

  private _isRequested: boolean = Boolean(false);

  constructor(@Inject(DOCUMENT) private readonly _document: Document) {}

  public get loaded$(): Observable<void> {
    return this._loaded$.asObservable();
  }

  public load(key: string): void {
    if (this._isRequested) {
      return;
    }
    this._isRequested = true;

    if (
      typeof google !== 'undefined' &&
      typeof google.maps !== 'undefined' &&
      typeof google.maps.places !== 'undefined'
    ) {
      this._loaded$.next();
      return;
    }

    // With `loading=async`, script.onload fires before the API is usable;
    // the `callback` param is the documented ready signal.
    const win = this._document.defaultView as any;
    win.__vitayGooglePlacesLoaded = () => this._loaded$.next();

    const script = this._document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&language=en&loading=async&callback=__vitayGooglePlacesLoaded`;

    this._document.getElementsByTagName('head')[0].appendChild(script);
  }
}
